import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const textRef = useRef(null);
  const skillsRef = useRef(null);
  const marqueeRef = useRef(null);

  const skills = [
    "React",
    "Next.js",
    "Node.js",
    "Golang",
    "Flutter",
    "Figma",
    "Tailwind CSS",
    "UI/UX Design",
    "Prototyping",
    "Responsive Design",
    "Design Systems",
  ];

  const services = [
    {
      number: "01",
      title: "Web Development",
      description:
        "Building modern, responsive, and scalable web applications using React, Next.js, and cutting-edge frontend and backend technologies.",
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
          <polyline points="7 8 10 11 7 14" />
          <line x1="13" y1="14" x2="17" y2="14" />
        </svg>
      ),
      accent: "#c9ff00",
    },
    {
      number: "02",
      title: "Mobile Apps",
      description:
        "Crafting cross-platform mobile applications with clean architecture, intuitive interfaces, and smooth user experiences.",
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
          <path d="M9 6h6" />
        </svg>
      ),
      accent: "#00fff0",
    },
    {
      number: "03",
      title: "UI/UX Design",
      description:
        "Designing user-centered interfaces and experiences through research, wireframing, prototyping, and usability testing.",
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 19l7-7 3 3-7 7-3-3z" />
          <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
          <path d="M2 2l7.586 7.586" />
          <circle cx="11" cy="11" r="2" />
        </svg>
      ),
      accent: "#c9ff00",
    },
  ];

  const processSteps = [
    {
      number: "01",
      title: "Planning",
      description: "Scoping the project, defining requirements, and aligning on goals before writing a single line of code.",
    },
    {
      number: "02",
      title: "Design",
      description: "Creating wireframes, prototypes, and polished visual designs ready for implementation.",
    },
    {
      number: "03",
      title: "Development",
      description: "Building the product with clean, maintainable code and modern technologies.",
    },
    {
      number: "04",
      title: "Testing",
      description: "Thorough QA, bug fixes, and performance optimization before going live.",
    },
    {
      number: "05",
      title: "Deployment",
      description: "Shipping to production, monitoring, and iterating based on real-world feedback.",
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Heading reveal animation
      gsap.from(headingRef.current, {
        y: 100,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 80%",
          end: "top 50%",
          toggleActions: "play none none reverse",
        },
      });

      // Text reveal animation
      gsap.from(textRef.current, {
        y: 50,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: textRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      // Services animation
      const serviceCards = document.querySelectorAll(".service-card");
      serviceCards.forEach((card, index) => {
        gsap.from(card, {
          y: 80,
          opacity: 0,
          duration: 1,
          delay: index * 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        });
      });

      // Process steps animation
      const processCards = document.querySelectorAll(".process-step");
      processCards.forEach((card, index) => {
        gsap.from(card, {
          y: 60,
          opacity: 0,
          duration: 1,
          delay: index * 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        });
      });

      // Parallax effect on heading
      gsap.to(headingRef.current, {
        yPercent: -20,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative py-32 md:py-48 bg-[#0a0a0a] overflow-hidden"
    >
      {/* Background accent */}
      <div className="circle-accent top-1/2 right-0 translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-20 md:mb-32">
          <span className="section-label">About the Studio</span>
          <h2 ref={headingRef} className="section-title max-w-4xl">
            We design and build{" "}
            <span className="text-gradient">digital products</span> from
            concept to launch
          </h2>
        </div>

        {/* Two column layout */}
        <div className="grid md:grid-cols-2 gap-16 md:gap-24 mb-32">
          {/* Left column - Description */}
          <div ref={textRef}>
            <p className="text-lg md:text-xl text-[#888] leading-relaxed mb-8">
              Baystrix Studio is an independent digital studio focused on
              designing and building digital products that work. We combine 
              strong design thinking with solid engineering to deliver 
              practical, well-crafted solutions.
            </p>
            <p className="text-lg md:text-xl text-[#888] leading-relaxed mb-12">
              From mobile app design to fullstack web development, we handle 
              every phase of the build lifecycle — design, development, 
              testing, and deployment. No fluff, just focused execution.
            </p>
            <a href="#contact" className="btn-outline magnetic">
              <span>Start a Project</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 11L11 1M11 1H1M11 1V11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </a>
          </div>

          {/* Right column - Capabilities */}
          <div>
            <h3 className="text-sm font-medium tracking-[0.2em] uppercase text-[#888] mb-8">
              Capabilities & Tools
            </h3>
            <div ref={skillsRef} className="flex flex-wrap gap-3">
              {skills.map((skill, index) => (
                <span key={index} className="skill-item">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="divider mb-32" />

        {/* Services */}
        <div className="mb-20">
          <span className="section-label">Services</span>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-32">
          {services.map((service, index) => (
            <div
              key={index}
              className="service-card group relative p-8 md:p-10 border border-[#222] rounded-2xl hover:border-[#c9ff00]/50 transition-all duration-500 hover:bg-[#111] overflow-hidden"
            >
              {/* Background accent glow on hover */}
              <div
                className="absolute -top-20 -right-20 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                style={{
                  background: `radial-gradient(circle, ${service.accent}10 0%, transparent 70%)`,
                }}
              />

              {/* Icon */}
              <div
                className="w-14 h-14 rounded-xl border border-[#333] flex items-center justify-center mb-6 text-[#888] group-hover:text-[#c9ff00] group-hover:border-[#c9ff00]/30 group-hover:bg-[#c9ff00]/5 transition-all duration-500"
              >
                {service.icon}
              </div>

              {/* Number */}
              <span className="text-4xl md:text-5xl font-display font-bold text-[#1a1a1a] group-hover:text-[#c9ff00]/20 transition-colors duration-500 absolute top-6 right-8">
                {service.number}
              </span>

              <h3 className="text-xl md:text-2xl font-display font-semibold mt-2 mb-4 relative">
                {service.title}
              </h3>
              <p className="text-[#888] leading-relaxed relative">
                {service.description}
              </p>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-[#c9ff00] to-[#00fff0] group-hover:w-full transition-all duration-700" />
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="divider mb-32" />

        {/* Process */}
        <div className="mb-20">
          <span className="section-label">Process</span>
        </div>

        <div className="grid md:grid-cols-5 gap-6 md:gap-4">
          {processSteps.map((step, index) => (
            <div
              key={index}
              className="process-step group text-center p-6 md:p-8 border border-[#222] rounded-2xl hover:border-[#c9ff00]/40 transition-all duration-500 hover:bg-[#111] relative"
            >
              {/* Step connector */}
              {index < processSteps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[1px] bg-[#333]">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#333]" />
                </div>
              )}
              <div className="w-10 h-10 rounded-full border border-[#333] flex items-center justify-center mx-auto mb-4 group-hover:border-[#c9ff00] group-hover:bg-[#c9ff00]/10 transition-all duration-500">
                <span className="text-sm font-display font-bold text-[#555] group-hover:text-[#c9ff00] transition-colors duration-500">
                  {step.number}
                </span>
              </div>
              <h3 className="text-lg font-display font-semibold mt-2 mb-3">
                {step.title}
              </h3>
              <p className="text-sm text-[#888] leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee */}
      <div ref={marqueeRef} className="mt-32 md:mt-48 py-8 border-y border-[#222]">
        <div className="marquee">
          <div className="marquee-content">
            {[...Array(2)].map((_, i) => (
              <React.Fragment key={i}>
                <span className="text-4xl md:text-6xl font-display font-bold text-[#222]">
                  WEB DEVELOPMENT
                </span>
                <span className="text-[#c9ff00] text-4xl">★</span>
                <span className="text-4xl md:text-6xl font-display font-bold text-[#222]">
                  MOBILE APPS
                </span>
                <span className="text-[#c9ff00] text-4xl">★</span>
                <span className="text-4xl md:text-6xl font-display font-bold text-[#222]">
                  UI/UX DESIGN
                </span>
                <span className="text-[#c9ff00] text-4xl">★</span>
                <span className="text-4xl md:text-6xl font-display font-bold text-[#222]">
                  FULLSTACK DEV
                </span>
                <span className="text-[#c9ff00] text-4xl">★</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
